import * as semver from 'semver';
import { z } from 'zod';
/**
 * Max length for the extension name segment (after the **last** `.`) in a composite id.
 * Publisher may contain `.` (domain-style); the extension segment must not contain `.`.
 */
export const EXTENSION_NAME_SEGMENT_MAX_LENGTH = 214;
/**
 * Publisher name for policy allow/block (literal, no wildcards).
 * Lowercase letters, digits, hyphens; optional dot-separated segments (domain-style).
 * 2–30 characters; no underscores; no consecutive dots; cannot start/end with `.` or `-`.
 * Each dot-separated label must start and end with an alphanumeric (hyphens only inside labels),
 * matching the extension segment rules in {@link isValidExtensionCompositeId}.
 */
export function isValidPublisherName(raw) {
    const t = raw.trim().toLowerCase();
    if (t.length < 2 || t.length > 30) {
        return false;
    }
    if (t.includes('..') || t.startsWith('.') || t.endsWith('.')) {
        return false;
    }
    if (!/^[a-z0-9.-]+$/.test(t)) {
        return false;
    }
    if (!/^[a-z0-9]/.test(t) || !/[a-z0-9]$/.test(t)) {
        return false;
    }
    const parts = t.split('.');
    for (const p of parts) {
        if (p.length === 0 || !/^[a-z0-9-]+$/.test(p)) {
            return false;
        }
        if (!/^[a-z0-9]/.test(p) || !/[a-z0-9]$/.test(p)) {
            return false;
        }
    }
    return true;
}
/**
 * Composite extension id `publisher.extension` (literal, no wildcards).
 * Split on the **last** `.` so a domain-style publisher (`company.team`) can prefix a
 * single extension segment
 * ({@link EXTENSION_NAME_SEGMENT_MAX_LENGTH} applies to that segment only).
 * Publisher segment must satisfy {@link isValidPublisherName}.
 * Extension segment: marketplace-style [a-z0-9-]+, 1–214 chars, must start and end with alphanumeric.
 */
export function isValidExtensionCompositeId(raw) {
    const t = raw.trim().toLowerCase();
    const dot = t.lastIndexOf('.');
    if (dot < 1 || dot === t.length - 1) {
        return false;
    }
    const pub = t.slice(0, dot);
    const ext = t.slice(dot + 1);
    if (ext.length > EXTENSION_NAME_SEGMENT_MAX_LENGTH) {
        return false;
    }
    if (!isValidPublisherName(pub)) {
        return false;
    }
    if (!/^[a-z0-9-]+$/.test(ext)) {
        return false;
    }
    if (!/^[a-z0-9]/.test(ext) || !/[a-z0-9]$/.test(ext)) {
        return false;
    }
    return true;
}
/**
 * Legacy list entry: `publisher.extension` or `publisher.extension@versionConstraint`.
 * Only the first `@` separates id from version (per {@link parseExtensionPolicyEntry}).
 */
export function isValidExtensionPolicyEntry(raw) {
    const trimmed = raw.trim();
    const at = trimmed.indexOf('@');
    const idPart = at === -1 ? trimmed : trimmed.slice(0, at);
    const verPart = at === -1 ? '*' : trimmed.slice(at + 1).trim() || '*';
    const idNorm = idPart.trim().toLowerCase();
    return (isValidExtensionCompositeId(idNorm) &&
        (verPart === '*' || isValidVersionConstraint(verPart)));
}
/**
 * Parses a policy extension list entry (first `@` only).
 */
export function parseExtensionPolicyEntry(entry) {
    const trimmed = entry.trim();
    const at = trimmed.indexOf('@');
    if (at === -1) {
        return {
            id: trimmed.toLowerCase(),
            versionConstraint: '*',
        };
    }
    const id = trimmed.slice(0, at).trim().toLowerCase();
    const ver = trimmed.slice(at + 1).trim();
    return {
        id,
        versionConstraint: ver.length === 0 ? '*' : ver,
    };
}
/**
 * Version constraint for extension policy: `*` or a range accepted by npm `semver.validRange`.
 * Whitespace-only strings are delegated to semver (e.g. `validRange('')` is non-null in npm semver).
 */
export function isValidVersionConstraint(constraint) {
    if (constraint === '*') {
        return true;
    }
    const s = constraint.trim();
    try {
        return semver.validRange(s) !== null;
    }
    catch {
        return false;
    }
}
function normalizeExtensionEntryString(entry) {
    const { id, versionConstraint } = parseExtensionPolicyEntry(entry);
    if (versionConstraint === '*') {
        return id;
    }
    return `${id}@${versionConstraint}`;
}
/**
 * Placeholder some UIs use for an empty in-progress row in legacy extension list editors.
 * Not a valid extension list entry. {@link stripExtensionListUiPlaceholders} removes it (and blank strings)
 * before {@link extensionEntrySchema} runs on each item.
 */
export const EXTENSION_LIST_EMPTY_ROW_SENTINEL = '__empty_extension_row__';
/**
 * Removes {@link EXTENSION_LIST_EMPTY_ROW_SENTINEL} and blank strings from a raw array input.
 */
export function stripExtensionListUiPlaceholders(raw) {
    if (!Array.isArray(raw)) {
        return raw;
    }
    return raw.filter((item) => typeof item === 'string' &&
        item.trim() !== '' &&
        item !== EXTENSION_LIST_EMPTY_ROW_SENTINEL);
}
export const publisherNameSchema = z
    .string()
    .superRefine((val, ctx) => {
    const t = val.trim().toLowerCase();
    if (!isValidPublisherName(t)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid publisher name (lowercase letters, digits, hyphens; optional dot-separated segments; 2-30 characters; no underscores)',
        });
    }
})
    .transform((val) => val.trim().toLowerCase());
export const extensionEntrySchema = z
    .string()
    .superRefine((val, ctx) => {
    if (!isValidExtensionPolicyEntry(val)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid extension entry: expected publisher.extension or publisher.extension@version (semver range or *)',
        });
    }
})
    .transform((val) => normalizeExtensionEntryString(val));
//# sourceMappingURL=extension-validators.js.map